import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Users, UserX, ShieldCheck, Clock, Loader2, RefreshCw } from "lucide-react";
import { Panel, Pill } from "./ui";
import { gateApi, useNexsysGate } from "./gate";

function relTime(iso) {
  if (!iso) return "—";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${Math.floor(s)}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export function AccessLog() {
  const { identity } = useNexsysGate();
  const [data, setData] = useState(null);
  const [denied, setDenied] = useState(false);
  const [kicking, setKicking] = useState("");

  const load = () => gateApi.sessions().then((r) => {
    if (r.status === 403 || r.status === 401) { setDenied(true); return null; }
    return r.ok ? r.json() : null;
  }).then((d) => d && setData(d)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, []);

  const kick = async (id) => {
    setKicking(id);
    try { await gateApi.kick(id); await load(); } catch { /* ignore */ } finally { setKicking(""); }
  };

  if (denied) {
    return (
      <div className="space-y-5" data-testid="access-log">
        <Panel title="Access Log" subtitle="RESTRICTED">
          <div className="flex items-center gap-3 py-6 text-white/60" data-testid="access-denied">
            <ShieldCheck className="h-5 w-5 text-amber-300" /> This area is available to administrators only.
          </div>
        </Panel>
      </div>
    );
  }

  const active = data?.active || [];
  const history = data?.history || [];

  return (
    <div className="space-y-5" data-testid="access-log">
      <Panel title="Access Log" subtitle="WHO'S IN YOUR COMMAND CENTER · LIVE"
        right={<Pill tone="emerald"><Users className="h-3 w-3" /> {data ? data.activeCount : "…"} active</Pill>}>
        <p className="text-[13px] leading-relaxed text-white/55">
          Everyone who enters with the access password is listed here. You can end anyone's session instantly —
          they'll be bounced back to the password screen within seconds.
        </p>
      </Panel>

      <Panel title="Currently Active" subtitle="LIVE SESSIONS" right={<button onClick={load} className="flex items-center gap-1 rounded-lg border border-white/10 px-2.5 py-1 text-[11px] text-white/60 hover:text-white" data-testid="access-refresh"><RefreshCw className="h-3 w-3" /> Refresh</button>}>
        {!data && <div className="flex items-center gap-2 py-6 text-white/50"><Loader2 className="h-5 w-5 animate-spin text-cyan-300" /> Loading…</div>}
        {data && active.length === 0 && <p className="py-4 text-[13px] text-white/45">No one else is active right now.</p>}
        <div className="space-y-2">
          {active.map((s) => {
            const you = s.id === data.you;
            return (
              <motion.div key={s.id} initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }}
                className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border border-white/[0.06] bg-white/[0.02] px-3 py-2.5" data-testid="active-session-row">
                <span className="h-2 w-2 rounded-full bg-emerald-400 nx-pulse" style={{ boxShadow: "0 0 8px #34d399" }} />
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-cyan-400 to-indigo-500 text-[11px] font-bold text-black">
                  {(s.name || "?").slice(0, 2).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-[13px] font-semibold text-white/90">
                    {s.name}{you && <span className="font-mono text-[9px] text-cyan-300">(you)</span>}
                    {s.is_admin && <span className="flex items-center gap-0.5 rounded-md bg-indigo-400/15 px-1.5 py-0.5 font-mono text-[9px] text-indigo-200"><ShieldCheck className="h-2.5 w-2.5" /> ADMIN</span>}
                  </div>
                  <div className="truncate font-mono text-[10px] text-white/40">{s.email} · {s.ip}</div>
                </div>
                <span className="ml-auto font-mono text-[10px] text-white/35">since {relTime(s.login_at)}</span>
                {!you && (
                  <button onClick={() => kick(s.id)} disabled={kicking === s.id}
                    className="flex items-center gap-1 rounded-lg border border-red-400/30 bg-red-500/10 px-2.5 py-1 text-[11px] font-medium text-red-300 hover:bg-red-500/20 disabled:opacity-50" data-testid={`kick-${s.id}`}>
                    {kicking === s.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <UserX className="h-3 w-3" />} Kick
                  </button>
                )}
              </motion.div>
            );
          })}
        </div>
      </Panel>

      <Panel title="Login History" subtitle="PAST SESSIONS" right={<Pill tone="indigo"><Clock className="h-3 w-3" /> {history.length}</Pill>}>
        {data && history.length === 0 && <p className="py-4 text-[13px] text-white/45">No past sessions yet.</p>}
        <div className="space-y-1.5" data-testid="access-history">
          {history.map((s) => (
            <div key={s.id} className="flex flex-wrap items-center gap-x-3 gap-y-0.5 rounded-lg border border-white/[0.05] bg-white/[0.01] px-3 py-2" data-testid="history-row">
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: s.revoked ? "#ef4444" : "#64748b" }} />
              <span className="text-[12px] font-medium text-white/80">{s.name}</span>
              <span className="min-w-0 truncate font-mono text-[10px] text-white/35">{s.email}</span>
              {s.revoked && <span className="font-mono text-[9px] uppercase text-red-300">kicked</span>}
              <span className="ml-auto font-mono text-[10px] text-white/30">{relTime(s.login_at)}</span>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}
